import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Typography,
  Divider,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const DetailsDialog = ({ open, onClose, selectedRow }) => {
  const theme = useTheme();
  // You can tune the breakpoint; 'sm' works well for phones
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const headers = ['Warehouse', 'WIP', 'FA', 'PO', 'REQ', 'WOs', 'YTD', 'LYTD', 'Min', 'Max'];
  const values = selectedRow
    ? [
        selectedRow.warehouse,
        selectedRow.wip,
        selectedRow.fa,
        selectedRow.ord_qty,
        selectedRow.req_qty,
        selectedRow.wos,
        selectedRow.ytd,
        selectedRow.ytd2,
        selectedRow.min_qty,
        selectedRow.max_qty,
      ]
    : [];

  // Helper to pair headers with values for the mobile layout
  const rows = headers.map((label, i) => ({ label, value: values[i] ?? '-' }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // Go full screen on mobile for better UX
      // fullScreen={isMobile}
      aria-labelledby="details-dialog-title"
    >
      {!!selectedRow && (
        <>
          <DialogContent sx={{ pt: 2 }}>
            {/* Summary Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                WCA Item #: {selectedRow.item_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Product Type: {selectedRow.prod_type}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Description: {selectedRow.description}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Data Section */}
            {isMobile ? (
              // --- Mobile: stacked label/value rows ---
              <Box
                component="section"
                aria-label="Item details"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  rowGap: 1.25,
                }}
              >
                {rows.map(({ label, value }) => (
                  <Box
                    key={label}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'text.secondary', pr: 2 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: 'text.primary', textAlign: 'right' }}
                    >
                      {value ?? '-'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              // --- Desktop/Tablet: original table ---
              <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 650 }} size="small" aria-label="Item details table">
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => (
                        <TableCell
                          key={header}
                          align="right"
                          sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {values.map((val, idx) => (
                        <TableCell key={idx} align="right">
                          {val ?? '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </DialogContent>
        </>
      )}
    </Dialog>
  );
};

export default DetailsDialog;
